import { Component, inject, signal } from "@angular/core";
import { WINDOW_TOKEN } from "@kno2/core/browser";
import { APP_CONFIG_TOKEN } from "@kno2/core/config";
import { SessionStorageService } from "@kno2/core/storage";
import { IdProofingService } from "@kno2/data-access/id-proofing";
import { DialogBodyComponent, DialogFooterComponent } from "@kno2/shared/dialog";
import { NotificationAlertComponent } from "@kno2/shared/notification-alert";
import { ID_PROOFING_ERROR_STORAGE_KEY } from "./id-proofing.constants";

@Component({
    selector: "kno2-onboarding-id-proofing-start-modal",
    imports: [DialogBodyComponent, DialogFooterComponent, NotificationAlertComponent],
    templateUrl: "./onboarding-id-proofing-start-modal.component.html",
    styles: [
        `
            .clear-button {
                background-color: #041a55;
                width: 236px;
                height: 40px;
                border-radius: 32px;
                border: none;
                color: #ffffff;
                font-family: "Inter", sans-serif;
                font-weight: 600;
                font-size: 16px;
                line-height: 26px;
                letter-spacing: -0.02em;
            }

            .clear-button:disabled {
                opacity: 0.5;
                cursor: not-allowed;
            }

            .clear-button:not(:disabled) {
                cursor: pointer;
            }

            .clear-logo {
                width: 24px;
                height: 24px;
                margin-right: 8px;
            }
        `
    ]
})
export class OnboardingIdProofingStartModalComponent {
    private readonly appConfig = inject(APP_CONFIG_TOKEN);
    private readonly idProofingService = inject(IdProofingService);
    private readonly sessionStorageService = inject(SessionStorageService);
    private readonly window = inject(WINDOW_TOKEN);

    protected readonly error = signal<string | null>(this.consumePersistedError());
    protected readonly isSubmitting = signal<boolean>(false);
    protected readonly brandDisplayName = signal(this.appConfig.brandDisplayName);

    protected async startIdentityProofing(): Promise<void> {
        try {
            this.error.set(null);
            this.isSubmitting.set(true);
            const response = await this.idProofingService.initiateAsync();
            this.window.location.href = response.authUrl;
        } catch {
            this.error.set("Failed to start the identity verification process.");
            this.isSubmitting.set(false);
        }
    }

    private consumePersistedError(): string | null {
        const persisted = this.sessionStorageService.get<string>(ID_PROOFING_ERROR_STORAGE_KEY);
        if (persisted) {
            this.sessionStorageService.remove(ID_PROOFING_ERROR_STORAGE_KEY);
            return persisted;
        }
        return null;
    }
}
